import { FrameData, GrenadeData, PlayerFrameData, ProjectileData, RoundData, Side, WeaponFireData } from "./types"
import { world_to_screen } from "./math"
import { mapInfo } from "./maps"

export interface ProjectileInfo {
  matchId: string
  mapName: string
  roundNum: number
  teamKey: string
  opponentKey: string
  minZ: number
  maxZ: number
  endZ: number
  startTick: number
  endTick: number
  endPosition: [number, number, number]
  turnOnTick: number
  turnOffTick: number
  angularVelocity: number
  startAngle: number
  side: Side
  thrower: PlayerFrameData
  throwerPosition: [number, number, number]
  visualPositions: [number | undefined, number | undefined][]
  visualPositionsFlat: [number | undefined, number | undefined][]
  positions: [number | undefined, number | undefined][]
  projectileType: string
  fireUniqueID: number | undefined
  uniqueID: number
}

export interface CachedProjectiles {
  [key: number]: ProjectileInfo
}

export interface Projectile {
  data: ProjectileData
  throwerPosition: [number, number, number]
  positions: [number | undefined, number | undefined][]
  visualPositions: [number | undefined, number | undefined][]
  visualPositionsFlat: [number | undefined, number | undefined][]
  minZ: number
  maxZ: number
  endZ: number
  startTick: number
  endTick: number
  currentFrameIndex: number
  angularVelocity: number
  startAngle: number
  side: Side
}

export interface PotentialThrower {
  steamID: number,
  distance: number,
  position: [number, number, number],
  tick: number,
}

export function findOutProjectileThrowerSteamID(projectile: ProjectileData, weaponFires: WeaponFireData[], players: PlayerFrameData[]): PotentialThrower | null {
  const potentialThrowers: PotentialThrower[] = [];

  for (const weaponFire of weaponFires) {
    if (weaponFire.weaponClass === "Grenade" && weaponFire.weapon === projectile.projectileType) {
      const distance = Math.sqrt(Math.pow(weaponFire.playerX - projectile.x, 2) + Math.pow(weaponFire.playerY - projectile.y, 2) + Math.pow(weaponFire.playerZ - projectile.z, 2));
      potentialThrowers.push({
        steamID: weaponFire.playerSteamID,
        distance,
        position: [weaponFire.playerX, weaponFire.playerY, weaponFire.playerZ],
        tick: weaponFire.tick,
      });
    }
  }
  potentialThrowers.sort((a, b) => a.distance - b.distance);
  return potentialThrowers.length > 0 ? potentialThrowers[0] : null;
}

export function precalculateBlindedPlayers(round: RoundData) {
  // A map to hold the end blinded tick for each player.
  const endBlindedTickMap = new Map<number, number>();

  // Iterate through the frames backwards
  for (let i = round.frames.length - 1; i >= 0; i--) {
    const frame = round.frames[i];
    const players = frame.t.players.concat(frame.ct.players);

    for (const player of players) {
      if (player.isBlinded) {
        // If player is currently blinded and 'endBlindedTick' is not yet set, set it to current tick.
        if (!endBlindedTickMap.has(player.steamID)) {
          endBlindedTickMap.set(player.steamID, frame.tick);
        }

        // As the player is blinded, calculate 'blindedForTicks' as the difference between 'endBlindedTick' and current tick.
        player.blindedForTicks = endBlindedTickMap.get(player.steamID)! - frame.tick + 1;
      } else {
        // If the player is not blinded, 'blindedForTicks' is 0.
        player.blindedForTicks = 0;

        // If 'endBlindedTick' is set for the player, delete it as the player is not blinded now.
        if (endBlindedTickMap.has(player.steamID)) {
          endBlindedTickMap.delete(player.steamID);
        }
      }
    }
  }
}

function findNextProjectileInCache(playerSteamID: number, currentTick: number, cache: any): any {
  // Iterate through the cache to find the next projectile for the given player after the current tick
  for (let uniqueID in cache) {
    if (cache[uniqueID].thrower.steamID === playerSteamID && cache[uniqueID].startTick > currentTick) {
      return cache[uniqueID];
    }
  }
  return undefined;
}

function findPreviousProjectileInCache(playerSteamID: number, currentTick: number, cache: any): any {
  // Iterate through the cache to find the last projectile for the given player before the current tick
  let lastProjectile = undefined;
  for (let uniqueID in cache) {
    if (cache[uniqueID].thrower.steamID === playerSteamID && cache[uniqueID].startTick < currentTick) {
      if (!lastProjectile || cache[uniqueID].startTick > lastProjectile.startTick) {
        lastProjectile = cache[uniqueID];
      }
    }
  }
  return lastProjectile;
}

// POV-demo fix for duplicate projectiles
// Remove duplicate projectiles which have the same thrower, projectile type and start tick, keeping the one with longest flight time
function removeDuplicateProjectiles(cache: { [key: number]: ProjectileInfo }) {
  const duplicateGroups = new Map<string, number[]>();

  // Group potential duplicates by their key characteristics
  Object.entries(cache).forEach(([uniqueID, projectile]) => {
    const key = `${projectile.thrower.steamID}-${projectile.projectileType}-${projectile.startTick}`;
    if (!duplicateGroups.has(key)) {
      duplicateGroups.set(key, []);
    }
    duplicateGroups.get(key)?.push(Number(uniqueID));
  });

  // For each group of duplicates, keep only the one with longest flight time
  let totalDuplicatesRemoved = 0;
  duplicateGroups.forEach((uniqueIDs) => {
    if (uniqueIDs.length > 1) {
      // Sort by flight time (descending)
      uniqueIDs.sort((a, b) => {
        const flightTimeA = cache[a].endTick - cache[a].startTick;
        const flightTimeB = cache[b].endTick - cache[b].startTick;
        return flightTimeB - flightTimeA;
      });

      // Keep the first one (longest flight time) and remove the rest
      const [keepID, ...duplicateIDs] = uniqueIDs;
      duplicateIDs.forEach(id => {
        delete cache[id];
      });
      totalDuplicatesRemoved += duplicateIDs.length;
    }
  });

  if (totalDuplicatesRemoved > 0) {
    console.log(`Removed ${totalDuplicatesRemoved} duplicate projectiles`);
  }
}

export function calculateProjectileCache(round: RoundData, mapName: string): CachedProjectiles {
  const cache: CachedProjectiles = {};
  const m = mapInfo[mapName as keyof typeof mapInfo];

  for (let i = 0; i < round.frames.length; i++) {
    const frame = round.frames[i];
    const frameBefore = round.frames[i > 0 ? i - 1 : i];
    const frameEarlier = round.frames[i > 1 ? i - 2 : (i > 0 ? i - 1 : i)];
    const players = frame.t.players.concat(frame.ct.players);
    const weaponFires = round.weaponFires.filter(fire => fire.tick <= frame.tick && (fire.tick >= frameEarlier.tick || i < 2));

    for (const projectile of frame.projectiles) {
      if (!(projectile.uniqueID in cache)) {
        const potentialThrower = findOutProjectileThrowerSteamID(projectile, weaponFires, players);
        const thrower = players.find(player => player.steamID === potentialThrower?.steamID);
        const throwerSide = thrower ? thrower.side : undefined;
        if (thrower && potentialThrower) {
          cache[projectile.uniqueID] = {
            matchId: round.matchId || '',
            mapName: round.mapName?.replace('de_', '') || '',
            roundNum: round.roundNum || 0,
            teamKey: Side[throwerSide as keyof typeof Side] === Side.CT ? round.ctTeamKey || '' : round.tTeamKey || '',
            opponentKey: Side[throwerSide as keyof typeof Side] === Side.CT ? round.tTeamKey || '' : round.ctTeamKey || '',
            maxZ: projectile.z,
            minZ: projectile.z,
            endZ: projectile.z,
            startTick: potentialThrower.tick,
            endTick: frame.tick,
            endPosition: [projectile.x, projectile.y, projectile.z],
            turnOffTick: 0,
            turnOnTick: Infinity,
            angularVelocity: Math.random() - 0.5, startAngle: Math.random() - 0.5,
            side: Side[throwerSide as keyof typeof Side],
            thrower,
            throwerPosition: potentialThrower.position,
            visualPositions: round.frames.map(frame => [undefined, undefined]),
            visualPositionsFlat: round.frames.map(frame => [undefined, undefined]),
            positions: round.frames.map(frame => [undefined, undefined]),
            projectileType: projectile.projectileType,
            fireUniqueID: undefined,
            uniqueID: projectile.uniqueID,
          };
        }
      } else {
        const projectileBefore = frameBefore ? frameBefore.projectiles.find(p => p.uniqueID === projectile.uniqueID) : undefined;
        if (projectile.projectileType === "HE Grenade" || projectile.projectileType === "Flashbang") {
          // Find the explosion for the entityID
          const grenade: GrenadeData | undefined = round.grenades.find(grenade => grenade.entityId === projectile.uniqueID);
          if (grenade) {
            cache[projectile.uniqueID].endPosition = [grenade.grenadeX, grenade.grenadeY, grenade.grenadeZ];
            cache[projectile.uniqueID].endTick = grenade.destroyTick;
          }
        }
        if (projectile.z > cache[projectile.uniqueID].maxZ) {
          cache[projectile.uniqueID].maxZ = projectile.z;
        }
        if (projectile.z < cache[projectile.uniqueID].minZ) {
          cache[projectile.uniqueID].minZ = projectile.z;
        }
        if (frame.tick > cache[projectile.uniqueID].endTick && (!projectileBefore || projectile.z !== projectileBefore.z)) {
          cache[projectile.uniqueID].endTick = frame.tick;
          cache[projectile.uniqueID].endZ = Math.max(projectile.z, m.minZ);
          cache[projectile.uniqueID].endPosition = [projectile.x, projectile.y, projectile.z];
        }
        if (frame.tick > cache[projectile.uniqueID].endTick) {
          cache[projectile.uniqueID].turnOffTick = frame.tick;
        }
      }
    }
  }

  // Find nextProjectileThrowTicks for each player
  for (let i = 0; i < round.frames.length; i++) {
    const frame = round.frames[i];
    const players = frame.t.players.concat(frame.ct.players);
    for (const player of players) {
      const nextProjectileInCache = findNextProjectileInCache(player.steamID, frame.tick, cache);
      player.nextProjectileThrowTick = nextProjectileInCache ? nextProjectileInCache.startTick : undefined;
      const previousProjectileInCache = findPreviousProjectileInCache(player.steamID, frame.tick, cache);
      player.prevProjectileThrowTick = previousProjectileInCache ? previousProjectileInCache.startTick : undefined;

    }
  }

  // Caching the projectile trails
  const notFound: any = {};
  const fakeDepthProjectionRatio = 1 / 200; // This needs to match with SVGProjectile 
  for (let i = 0; i < round.frames.length; i++) {
    const frame = round.frames[i];
    for (const projectile of frame.projectiles) {
      if (projectile.uniqueID in cache) {
        const screen = world_to_screen([projectile.x, projectile.y, projectile.z], mapName)
        const z = Math.max(projectile.z - cache[projectile.uniqueID].minZ, 0); // This needs to match with SVGProjectile 
        cache[projectile.uniqueID].visualPositionsFlat[i] = [screen[0], screen[1]];
        cache[projectile.uniqueID].visualPositions[i] = [screen[0] - z * fakeDepthProjectionRatio, screen[1] - z * fakeDepthProjectionRatio];
        cache[projectile.uniqueID].positions[i] = [projectile.x, projectile.y];
      } else {
        notFound[projectile.uniqueID] = true;
      }
    }
  }
  for (const id of Object.keys(notFound)) {
    console.log(`Warning: Weaponfire for projectile ${id} not found`);
  }

  // Figuring out the fire uniqueID of fire projectile
  const fireProjectiles = Object.keys(cache).map(Number).filter(id => cache[id].projectileType === "Incendiary Grenade" || cache[id].projectileType === "Molotov");
  let usedFireUniqueIDs = new Set();
  for (const projectileUniqueID of fireProjectiles) {
    const projectile = cache[projectileUniqueID];
    let potential = { distance: Infinity, uniqueID: -1 };
    for (let i = 0; i < round.frames.length; i++) {
      const frame = round.frames[i];

      for (const fire of frame.fires.filter(fire => !usedFireUniqueIDs.has(fire.uniqueID))) {
        const distance = Math.sqrt((fire.x - projectile.endPosition[0]) ** 2 + (fire.y - projectile.endPosition[1]) ** 2);
        const timeDiff = frame.tick - projectile.endTick;
        if (distance < potential.distance && projectile.endTick <= frame.tick && timeDiff < 128 && timeDiff > 0) {
          potential = { distance, uniqueID: fire.uniqueID };
        }
      }
    }
    if (potential.uniqueID !== -1) {
      usedFireUniqueIDs.add(potential.uniqueID);
      cache[projectileUniqueID].fireUniqueID = potential.uniqueID;
      for (let i = 0; i < round.frames.length; i++) {
        const frame = round.frames[i];
        const fire = frame.fires.find(fire => fire.uniqueID === potential.uniqueID);
        if (fire) {
          if (cache[projectileUniqueID].turnOnTick > frame.tick) {
            cache[projectileUniqueID].endPosition = [fire.x, fire.y, fire.z];
            cache[projectileUniqueID].turnOnTick = frame.tick;
            const screen = world_to_screen([fire.x, fire.y, fire.z], mapName)
            const z = Math.max(fire.z - cache[projectileUniqueID].minZ, 0);
            cache[projectileUniqueID].visualPositionsFlat[i] = [screen[0], screen[1]];
            cache[projectileUniqueID].visualPositions[i] = [screen[0] - z * fakeDepthProjectionRatio, screen[1] - z * fakeDepthProjectionRatio];
            cache[projectileUniqueID].positions[i] = [fire.x, fire.y];
          }
          if (cache[projectileUniqueID].turnOffTick < frame.tick) {
            cache[projectileUniqueID].turnOffTick = frame.tick;
          }
        }
      }
    }
  }

  // POV-demo fix for duplicate projectiles
  if (round.matchId?.includes("user-upload")) {
    removeDuplicateProjectiles(cache);
  }

  return cache;
}

export function getExplosionTick(grenade: GrenadeData, csVersion?: string | undefined) {
  return getExplosionTickRaw(grenade.throwTick, grenade.grenadeType, csVersion);
}

export function getExplosionTickRaw(throwTick: number, grenadeType: string, csVersion?: string | undefined) {
  const ticksPerSecond = csVersion === 'cs2' ? 64 : 128;
  // const heGrenadeFuseTime = 1.669;
  // const flashbangFuseTime = 1.701;
  const heGrenadeFuseTime = 1.72;
  const flashbangFuseTime = 1.77;
  const fuseTime = grenadeType === "HE Grenade" ? heGrenadeFuseTime : flashbangFuseTime;
  return throwTick + ticksPerSecond * fuseTime;
}

export const isProjectileInRange = (projectile: ProjectileInfo, minTick: number, maxTick: number) => {
  const { startTick, endTick } = projectile;
  return (startTick <= maxTick && startTick >= minTick) ||
    (endTick <= maxTick && endTick >= minTick);
};

function hash(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    let character = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + character;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash); // Ensure the value is positive
}

export function fixRoundDataUniqueIDs(data: RoundData) {
  // Collect a set of frames to be removed
  for (let frame of data.frames) {
    for (let projectile of frame.projectiles) {
      // take projectile.uniqueID and make sure it is unique across all matches and rounds. Use frame.matchId and frame.roundNum with the existing uniqueID to come up with new number hash
      let idStr = `${data.matchId}-${data.roundNum}-${projectile.uniqueID}`;
      projectile.uniqueID = hash(idStr); projectile.uniqueID = hash(idStr);
    }
  }
  for (let grenade of data.grenades) {
    let idStr = `${data.matchId}-${data.roundNum}-${grenade.entityId}`;
    grenade.entityId = hash(idStr);
  }
}

// This function is used to remove duplicate grenade weapon fires on POV demos where same grenade throw has multiple weapon fires in the following ticks
export function clearDuplicateGrenadeWeaponFires(data: RoundData) {
  const grenadeWeaponFires = data.weaponFires.filter(fire => fire.weaponClass === "Grenade");
  // Sort grenade weaponFires by tick
  grenadeWeaponFires.sort((a, b) => a.tick - b.tick);

  const duplicatesToRemove = new Set<WeaponFireData>();
  const DUPLICATE_WINDOW_TICKS = 32;

  for (let i = 0; i < grenadeWeaponFires.length; i++) {
    const currentWeaponFire = grenadeWeaponFires[i];

    // Skip if this weaponFire is already marked as duplicate
    if (duplicatesToRemove.has(currentWeaponFire)) {
      continue;
    }

    // Look ahead for duplicates within the window
    for (let j = i + 1; j < grenadeWeaponFires.length; j++) {
      const laterWeaponFire = grenadeWeaponFires[j];

      // Break if we're beyond the window
      if (laterWeaponFire.tick > currentWeaponFire.tick + DUPLICATE_WINDOW_TICKS) {
        break;
      }

      // Check if it's a duplicate (same player and weapon class)
      if (laterWeaponFire.playerSteamID === currentWeaponFire.playerSteamID &&
        laterWeaponFire.weaponClass === currentWeaponFire.weaponClass) {
        duplicatesToRemove.add(laterWeaponFire);
      }
    }
  }

  // Remove the duplicates from the original data
  data.weaponFires = data.weaponFires.filter(weaponFire => !duplicatesToRemove.has(weaponFire));
}

// POV-demo fix for fires which only last for single frame
export function clearExtraFires(data: RoundData) {
  // Keep track of how many frames each fire appears in
  const fireFrameCounts: { [key: number]: number } = {};

  // Count occurrences of each fire uniqueID across frames
  for (const frame of data.frames) {
    for (const fire of frame.fires) {
      fireFrameCounts[fire.uniqueID] = (fireFrameCounts[fire.uniqueID] || 0) + 1;
    }
  }

  // Get list of fire IDs that only appear once
  const singleFrameFireIDs = Object.entries(fireFrameCounts)
    .filter(([_, count]) => count === 1)
    .map(([id, _]) => parseInt(id));

  if (singleFrameFireIDs.length > 0) {
    console.log(`Removed ${singleFrameFireIDs.length} single-frame fires`);
  }

  // Remove fires that only appear in one frame
  for (const frame of data.frames) {
    frame.fires = frame.fires.filter(fire => !singleFrameFireIDs.includes(fire.uniqueID));
  }
}

export function generateRoundDataTeamKeys(data: RoundData, ctTeamKey: string, tTeamKey: string) {
  data.ctTeamKey = ctTeamKey;
  data.tTeamKey = tTeamKey;
}

export function offsetTicksToStartFromZero(data: RoundData) {
  const offset = data.frames[0].tick;
  for (let frame of data.frames) {
    frame.tick -= offset;
    for (let smoke of frame.smokes) {
      smoke.startTick -= offset;
    }
  }
  for (let kill of data.kills) {
    kill.tick -= offset;
  }
  for (let weaponFire of data.weaponFires) {
    weaponFire.tick -= offset;
  }
  for (let damages of data.damages) {
    damages.tick -= offset;
  }
  for (let grenade of data.grenades) {
    grenade.throwTick -= offset;
    grenade.destroyTick -= offset;
  }
  data.startTick -= offset;
  data.endTick -= offset;
}

export type ShapeData = {
  [key: string]: string;
};

export const projectileShapes: { [key: string]: ShapeData } = {
  "Smoke Grenade": {
    "main": "m0.2266 0.036337v-0.13361c0-0.02242 0.00193-0.048802-0.002574-0.06863-0.003284-0.01449-0.010084-0.019735-0.015424-0.030818-0.003653-0.007493-0.00387-0.012895-0.00387-0.022701 0-0.003779-0.007869-0.011148-0.008994-0.01327-0.00409-0.007462-0.005591-0.021015-0.010927-0.026351-0.011896-0.011896-0.007308-0.037094-0.019264-0.048802-0.005808-0.005652-0.003251-0.022481-0.00896-0.028258-0.005652-0.005621-0.009897-0.017703-0.012272-0.023761-0.0064-0.016985-0.021926-0.032137-0.027641-0.048622 8.8041e-4 -0.008994-0.006275-0.003559-0.009025-0.008994-0.001592-0.003047 0.002336-0.011801 0-0.014175-0.001457-0.001422-0.011304-4.4021e-4 -0.012864-0.001287-0.0093355 0.003529-0.018983 0.021357-0.024384 0.032128 0.0020999-0.014175-0.061011-0.007714-0.069597-0.007714h-0.061759c-0.0074634 0-0.019484-0.00237-0.026666 0-0.014737 0.004873-0.025947 0.053923-0.020483 0.053923 0.002607 0 0.032098-4.402e-4 0.032098 0.001862v0.048022c0 0.012332-0.0077443 0.01942-0.0077443 0.032785 0 0.007805-0.025667 0.008242-0.033471 0.009617-0.015893 0.002777-0.07706 9.346e-5 -0.060293 0.033628 6.874e-4 0.036563 0 0.073282 0 0.10991v0.51282c0 0.012739 0.038062 0.024855 0.048396 0.028945 0.067162 0.02626 0.13048 0.020419 0.19886 0.006183 0.022667-0.004714 0.050364-0.012647 0.068692-0.026541 0.005591-0.004217 0.007744-8.804e-4 0.007744-0.007525v-0.6225c0.004537 0.020008 0.01056 0.034742 0.022107 0.052802 0.002912 0.004466 0.013612 0.015705 0.015363 0.020544 0.006621 0.017672 0 0.050988 0 0.070097v0.40197c0 0.00659-0.007744 0.008492-0.007744 0.01989 0 0.010554-0.001388 0.010274 0.009055 0.010274 0.006962 0 0.02811-0.004486 0.031293-0.006721 0.012177-0.008397 0.010304-0.021607 0.010304-0.039186 3.115e-5 -0.088706 3.115e-5 -0.17729 3.115e-5 -0.26596z",
    "extra": "m-0.13048-0.15286-0.001107 0.068209c-1.5407e-4 0.0094907-0.00312 0.01501 0.007153 0.016297 0.047959 0.0059323 0.064748 0.0099964 0.11261 0.015989 0.012864 0.0016243 0.027588-0.0027159 0.040326-0.0042735 0.028413-0.003588 0.021854-0.0026393 0.050268-0.0062291 0.020019-0.0025296 0.02001 7.075e-5 0.020371-0.020534l0.0011072-0.063084c-7e-7 -0.001271-5.06e-6 -0.002584-3.621e-5 -0.003803-0.025041 0.002686-0.030582 0.003078-0.055718 0.005686-0.021732 0.002342-0.032785 0.00738-0.054704 0.005179-0.050457-0.00512-0.069878-0.008285-0.12027-0.013436zm0 0.37228-0.001107 0.068209c-1.5407e-4 0.009491-0.00312 0.01501 0.007153 0.016297 0.047959 0.005932 0.064748 0.009996 0.11261 0.015989 0.012864 0.001626 0.027588-0.002716 0.040326-0.004273 0.028413-0.003592 0.021854-0.002639 0.050268-0.006229 0.020019-0.00253 0.02001 7.076e-5 0.020371-0.020534l0.0011072-0.063084c-4.2e-7 -0.00127-5.06e-6 -0.002603-3.621e-5 -0.003821-0.025041 0.002686-0.030582 0.003096-0.055718 0.005704-0.021732 0.002345-0.032785 0.00738-0.054704 0.005179-0.050457-0.005119-0.069878-0.008285-0.12027-0.013437z",
  },
  "Molotov": {
    "main": "m0.055903-0.37058 0.0012239-0.074121-0.016924-0.011263-0.041005-0.004916c-0.015406 2.0207e-4 -0.028561 0.001741-0.039539 0.004743l-0.017462 0.010631-0.001187 0.074051 0.0011512-8.1479e-4 0.0050681 0.007161-0.013129 0.18092-0.011298 0.029401-0.023906 0.041952c-0.023964 0.041993-0.03567 0.075875-0.035215 0.10176l-8.6263e-4 0.43839c0.002458 0.010142 0.016368 0.020522 0.041809 0.03117 0.028069 0.011147 0.058912 0.016706 0.092315 0.016871 0.033519 8.205e-5 0.062594-0.004508 0.092676-0.015576 0.030031-0.011076 0.042054-0.031494 0.042054-0.031494l0.0015-0.43718c-4.6366e-4 -0.025838-0.012042-0.05997-0.034875-0.10217-0.024039-0.042481-0.03593-0.06687-0.035586-0.073054l-0.011951-0.18099z",
    "extra": "m0.056983-0.50545c0.0015935 0.007214 0.0082899 0.012607 0.020301 0.016035 0.011153 0.004028 0.017172 0.015802 0.017968 0.035504l-0.0061228 0.072259c-0.001663 0.016595-0.0099176 0.032092-0.023928 0.04655l-0.0017002-0.025487 0.0046544-0.005022 0.0013482-0.08561-0.022491-0.015054-0.04798-0.007061c-0.016694 2.3824e-4 -0.030665 0.001916-0.04269 0.00523l-0.026548 0.015509-0.0012995 0.080989 7.9723e-4 0.023593 0.0043954-0.003112-0.0037344 0.050825-0.036626-0.019593c-0.021498-0.016978-0.035056-0.033548-0.040664-0.04989l-0.004226-0.036278c-0.002349-0.014982-0.013039-0.030087-0.03203-0.045255l0.016524-0.004489c0.014217-0.001761 0.027952 0.001662 0.041093 0.01023l0.026383 0.029549 0.010419 0.01228-0.0099683-0.051916c-0.0048356-0.008459-0.018701-0.017167-0.04164-0.026004l-0.049749-0.014807c-0.009818-0.003754-0.023575-0.019049-0.041346-0.045825-0.018285-0.027462-0.0264-0.047362-0.024279-0.059608 0.013376 0.015565 0.030888 0.025185 0.052547 0.028943 0.010913 0.001843 0.033014-0.003096 0.066431-0.014825 0.013369-0.004684 0.024844-0.002731 0.034514 0.005929l0.027212 0.018265 0.025752 0.008549c0.010645 0.003256 0.016393 0.002117 0.017319-0.003301 9.5794e-4 -0.005398-0.0086928-0.012281-0.02882-0.020661-0.021598-0.00848-0.032423-0.02711-0.032389-0.055703 0.007233 0.006805 0.025169 0.011979 0.053873 0.015509 0.029237 0.00434 0.051512 0.013035 0.066696 0.026093l0.018002 0.022993 0.010738 0.035296z",
  },
  "Incendiary Grenade": {
    "main": "m0.13588-0.46645c0.006316 0.017962 0.044073 0.027921 0.051101 0.046425 0.002479 0.006552 0.007131 0.019726 0.013276 0.025838 0.006316 0.006282 0.003462 0.024582 0.009846 0.030727 0.012936 0.012731 0.00798 0.040166 0.020915 0.053068 0.005805 0.005773 0.007435 0.020541 0.011816 0.028656 0.001294 0.002309 0.009846 0.010323 0.009846 0.014429 0 0.010627 1.6981e-4 0.0165 0.004143 0.02465 0.005873 0.012087 0.013208 0.017758 0.016772 0.033545 0.00489 0.02156 0.002851 0.05025 0.002851 0.074628v0.43449c0 0.019115 0.001998 0.033511-0.011204 0.042611-0.00353 0.002411-0.033718 0.010854-0.041289 0.010854-0.01134 0-0.009846 2.9512e-4 -0.009846-0.011204 0-0.012393 0.00842-0.014464 0.00842-0.021594v-0.43711c0-0.020745 0.007199-0.056973 0-0.076224-0.001998-0.005262-0.013614-0.017519-0.016772-0.022341-0.005805-0.009031-0.009982-0.01993-0.016772-0.027943-0.006654-0.007878-0.011816-0.019964-0.020984-0.026517v0.67691c0 0.007267-0.002275 0.003632-0.008352 0.008216-0.019964 0.015074-0.050148 0.023699-0.074696 0.028826-0.074356 0.015517-0.14321 0.021898-0.21631-0.006688-0.011169-0.004413-0.052559-0.017621-0.052559-0.031508v-0.55764c0-0.039793 7.469e-4 -0.079755 0-0.11951-0.018232-0.036465 0.048281-0.033545 0.065563-0.036567 0.008488-0.001498 0.036397-0.001975 0.036397-0.010457 0-0.014566 0.008352-0.022273 0.008352-0.035684v-0.052219c0-0.002547-0.031983-0.002043-0.034835-0.002043-0.005941 0 0.006214-0.05334 0.022273-0.05867 0.007776-0.002547 0.020779 0 0.028996 0h0.067158c0.009269 0 0.077989-0.007029 0.07568 0.008386 0.005839-0.01168 0.016332-0.031033 0.026517-0.034903 0.001589 9.0808e-4 0.012359-1.6981e-4 0.013921 0.001385 0.002581 0.002581-0.001703 0.012087 0 0.015415",
    "extra": "m0.028606-0.24125c-0.026483 0.021424-0.053034 0.042814-0.079551 0.064238h-1.954e-5c-0.056633 0.049537-0.082935 0.13073-0.080898 0.20434 0.00193 0.069399 0.026246 0.15265 0.10889 0.15744-0.060334-0.037382-0.054894-0.13035-0.0014453-0.17041-0.010933 0.030829 0.0052481 0.060289 0.026367 0.082324 0.010593 0.01107 0.063359 0.066382 0.039219 0.082441 0.037348 0.014429 0.070817-0.055206 0.075469-0.080977 0.006722-0.043969-0.00461-0.086311-0.029395-0.1224-0.040777-0.059281-0.11642-0.14532-0.058633-0.21699zm-0.16293 0.46973v0.034902c0 0.010323-0.003432 0.016309 0.007773 0.017734 0.052015 0.006452 0.065983 0.009757 0.11807 0.016309 0.013989 0.001725 0.029985-0.002931 0.04377-0.004629 0.030863-0.003905 0.024787-0.003707 0.055684-0.007578 0.021867-0.002749 0.022207 6.015e-5 0.022207-0.022383v-0.033438l-0.044727 0.00457c-0.023665 0.002513-0.052513 0.010484-0.076348 0.008008-0.054833-0.005569-0.071592-0.007927-0.12643-0.013496z",
  },
  "Flashbang": {
    "main": "m-0.077609-0.34454v-0.0586c-0.02342-0.002934-0.035147-0.014151-0.035147-0.033719 0-0.012691 0.004385-0.020996 0.013155-0.024881l0.0293-0.004417h0.16115l0.13627 0.21826v0.46143l-0.068898 0.033719-0.021923-0.004386c-0.004881-0.008803-0.002973-0.017108 0.005813-0.024916 0.00781-0.006876 0.019568-0.014649 0.035213-0.023487l0.020464-0.010267v-0.41156l-0.058571-0.033685v0.068865l-0.035213 0.032224v0.068865h-0.014648v0.070293h0.014649v0.07325h-0.014649v0.070327h0.014649v0.07325h-0.014649v0.070293h0.014649v0.067404l0.035213 0.060062v0.04983c0 0.013653-0.006844 0.020464-0.020529 0.020464h-0.26078c-0.01372-6.642e-5 -0.020496-0.006876-0.020496-0.020529v-0.04983l0.033685-0.060062v-0.067404h0.016112v-0.070293h-0.016112v-0.07325h0.016112v-0.070293h-0.016112v-0.07325h0.016112v-0.07026h-0.016112v-0.068865l-0.033685-0.032224v-0.093747c0-0.013653 0.006776-0.020529 0.020463-0.020529h0.02053z",
    "extra": "",
  },
  "HE Grenade": {
    "main": "m0.17366-0.39531c0.003951 0.023207 0.020656 0.041088 0.03091 0.060919 0.013154 0.025434 0.023732 0.053167 0.035136 0.079525 0.009003 0.020881 0.018006 0.041538 0.030085 0.06067 0.011654 0.018381 0.01563 0.036987 0.014379 0.058468-0.00345 0.060394 0.003077 0.12104 0.003077 0.18143 0 0.028184 0.0026 0.057793-3.7456e-4 0.085851-9.2419e-4 0.008754-0.004701 0.01548-0.013505 0.017556-0.005477 0.001251-0.017631 0.002326-0.019456-0.005153-0.002374-0.009703 0.010204-0.010953 0.012204-0.018581 0.002651-0.010128 0-0.020956 0-0.03121 0-0.032835 1.5005e-4 -0.065646-7.5e-5 -0.098429-4.7616e-4 -0.057018-0.003251-0.11226-0.018081-0.16715-0.002224-0.008327-0.006277-0.021256-0.012528-0.027359-0.005251-0.005127-0.003876-0.01473-0.009403-0.019681-0.01603-0.014354-0.023257-0.034236-0.035211-0.053117-0.005477-0.008653-0.011628-0.01558-0.018156-0.023358-0.004427-0.005227-0.004252-0.01413-0.010078-0.017606-0.010628-0.006327-0.029309-0.0033-0.041563-0.003851-0.010503-5.0154e-4 -0.03166-0.003951-0.040488 0.003726-0.0046016 0.003977-0.0014253 0.022157-0.0014253 0.027909 0 0.009128-0.0045514 0.027909 0.0060268 0.03231 0.0079522 0.003352 0.017181 0.002876 0.025558 0.003525 0.013854 0.001076 0.027359 0.004577 0.040788 0.007902 0.024808 0.006176 0.039813 0.01548 0.057894 0.033486 0.020106 0.019981 0.023657 0.045815 0.032561 0.071548 0.010678 0.03086 0.01578 0.057718 0.011979 0.090576-0.005526 0.04759-0.028309 0.09728-0.058344 0.13432-0.037538 0.046289-0.1018 0.056618-0.15615 0.068872-0.027109 0.006101-0.056118 0.009953-0.083329 2e-3 -0.023458-0.006903-0.04704-0.01438-0.070347-0.021957-0.015605-0.005102-0.025583-0.016481-0.039388-0.025058-0.008252-0.005153-0.01373-0.012529-0.020656-0.019181-0.008578-0.0081525-0.01668-0.012179-0.022982-0.022932-0.027534-0.04694-0.040989-0.098182-0.039138-0.15285 0.001425-0.042064 0.012078-0.08538 0.038037-0.11854 0.013154-0.016931 0.023657-0.027934 0.043489-0.035537 0.021457-0.008228 0.042989-0.013029 0.064346-0.020382 0.012729-0.00435 0.037862 0.006101 0.041038-0.010979 0.0017017-0.009253 0.0047768-0.047215-0.0078281-0.047215-0.010578-5.002e-5 -0.0052264-0.12489-0.0052264-0.13777-0.013079 6.7613e-4 -0.028659 0.002374-0.041038-0.00305-0.005153-0.002224-0.008553-0.006402-0.007427-0.012229 7.2375e-4 -0.003951 0.002975-0.007627 0.005302-0.010853 0.0033-0.004651 0.002876-0.005277 0.005002-0.010054 0.0041509-0.009103 0.012179-0.011504 0.021357-0.011754 0.011479-2.7509e-4 0.015305-0.008327 0.026608-0.008327h0.099307c0.0041514 0 0.038137 0.001774 0.038737-9.0185e-4 0.0013751-0.006603-8.0001e-4 -0.013829-3.4917e-4 -0.020556 5.0155e-4 -0.007276 0.01443-0.00545 0.018956-0.005127 0.010204 7.2375e-4 0.014555 0.007552 0.014555 0.018531 0 4e-3 -0.005577 0.007252-0.001476 0.011029 0.003326 0.003077 0.007276 0.005352 0.010428 0.008653 0.006978 0.007228 0.007402 0.021332 0.013079 0.030235 0.013679 0.021232 0.015455 0.053367 0.033186 0.071698z",
    "extra": "",
  },
  "Decoy Grenade": {
    "main": "m-0.074096-0.33642-0.043712 0.037864h-0.020419c-0.013613 0-0.020353 0.006839-0.020353 0.020386v0.09324l0.033503 0.032049v0.068492h0.016024v0.069913h-0.016025v0.072821h0.016024v0.069946h-0.016024v0.072821h0.016024v0.069913h-0.016024v0.067006l-0.033503 0.05977v0.049527c0 0.013579 0.006741 0.020353 0.020353 0.020353h0.25937c0.013612 0 0.020419-0.006773 0.020419-0.020353v-0.049527l-0.035023-0.05977v-0.067006h-0.014571v-0.069913h0.01457v-0.072821h-0.01457v-0.069946h0.01457v-0.072788h-0.01457v-0.069913h0.01457v-0.068492l0.035023-0.032049v-0.068459l0.05825 0.033503v0.40934l-0.020353 0.010242c-0.015562 0.008788-0.027225 0.01652-0.035023 0.023359-0.008723 0.007731-0.010639 0.015992-0.005782 0.024747l0.021807 0.004395 0.068526-0.033536v-0.45899l-0.13553-0.21708h-0.16028l-0.029175 0.004428c-0.008723 0.003832-0.013084 0.012093-0.013084 0.024747 0 0.01946 0.011663 0.030629 0.034956 0.033536v0.05825",
    "extra": "m0.038031-0.2265 0.051076 0.028615-0.021185 0.036159-0.049914-0.028643v0.057343h-0.041037v-0.058649l-0.052381 0.029948-0.019965-0.036159 0.051105-0.028615-0.051105-0.02992 0.021185-0.036131 0.051162 0.02992v-0.059839h0.041037v0.058592l0.049914-0.028672 0.019937 0.036131z",
  }
}